import { Injectable } from '@angular/core';
import { Rule } from '../rule-base';
import { ModifierContext, ClaimLine, AdjustedLine } from '../modifier-models';
import { RuleProvider } from './base-rule-provider';
import { RulePhysio0001 } from '../rules/physio-rules/rule-physio-0001';
import { RulePhysio0003 } from '../rules/physio-rules/rule-physio-0003';
import { RulePhysio0008 } from '../rules/physio-rules/rule-physio-0008';
import { RulePhysio0009 } from '../rules/physio-rules/rule-physio-0009';
import { RulePhysio0010 } from '../rules/physio-rules/rule-physio-0010';
import { RulePhysio0013 } from '../rules/physio-rules/rule-physio-0013';
import { RulePhysio0014 } from '../rules/physio-rules/rule-physio-0014';
import { InfoValidatorRule } from '../rules/rules-generic';
import { CoreModifier } from '../modifier-models';

@Injectable({ providedIn: 'root' })
export class PhysiotherapyRuleProvider implements RuleProvider {
    readonly disciplineCode = '072';
    readonly disciplineName = 'Physiotherapy';

    getRules(): Rule[] {
        return [
            // ── physiotherapy-specific rules ──────────────────────────────────────────
            new RulePhysio0001(), // Appointment not kept - No charge
            new RulePhysio0003(), // 15% reduction for non-owned equipment
            new RulePhysio0008(), // 50% reduction for additional procedures
            new RulePhysio0009(), // Full fee for additional condition
            new RulePhysio0010(), // 50% reduction for second condition
            new RulePhysio0013(), // Travelling costs for distances over 16km
            new RulePhysio0014()  // In-patient physiotherapy services
        ];
    }

    canHandle(disciplineCode: string): boolean {
        return disciplineCode === '072';
    }

    getContext(lines: ClaimLine[]): ModifierContext {
        // Calculate totals for physiotherapy context
        const nonModifierLines = lines.filter(line => !line.isModifier);
        const totalPrice = nonModifierLines.reduce((sum, line) => sum + (line.initialRand || 0), 0);
        const totalUnits = nonModifierLines.reduce((sum, line) => sum + (line.initialUnits || line.units || 0), 0);

        return {
            totalPrice,
            totalUnits,
            lines: lines.map(l => ({
                ...l,
                adjustedUnits: l.initialUnits ?? l.units ?? 0,
                adjustedRand: l.initialRand ?? 0,
                trace: ['init']
            }))
        };
    }

    validateModifier(modifierCode: string, line: ClaimLine): boolean {
        // Physiotherapy modifiers validation logic
        const physioModifiers = [
            '0001', // Appointment not kept
            '0003', // 15% reduction for non-owned equipment
            '0008', // 50% reduction for additional procedures
            '0009', // Full fee for additional condition
            '0010', // 50% reduction for second condition
            '0013', // Travelling costs
            '0014'  // In-patient services
        ];
        
        return physioModifiers.includes(modifierCode);
    }

    /**
     * Process lines with attached modifiers for physiotherapy discipline
     * This method handles the case where modifiers are attached to procedure lines
     */
    processLinesWithModifiers(lines: ClaimLine[]): AdjustedLine[] {
        return lines.map(line => {
            if (!line.modifiers || line.modifiers.length === 0) {
                return {
                    ...line,
                    adjustedUnits: line.initialUnits ?? line.units ?? 0,
                    adjustedRand: line.initialRand ?? 0,
                    trace: ['init']
                };
            }

            let totalReduction = 1.0; // Start with no reduction
            const appliedModifiers: string[] = [];

            // Process each modifier attached to the line
            line.modifiers.forEach(modifierCode => {
                switch (modifierCode) {
                    case '0001' as CoreModifier:
                        // Appointment not kept - Fee may be charged but not payable by schemes
                        appliedModifiers.push('Physio 0001: Appointment not kept - Fee may be charged but not payable by schemes');
                        break;
                    
                    case '0003' as CoreModifier:
                        // 15% reduction for non-owned equipment
                        totalReduction *= 0.85;
                        appliedModifiers.push('Physio 0003: 15% reduction for non-owned equipment');
                        break;
                    
                    case '0008' as CoreModifier:
                        // 50% reduction for additional procedures
                        totalReduction *= 0.5;
                        appliedModifiers.push('Physio 0008: 50% reduction for additional procedures');
                        break;
                    
                    case '0009' as CoreModifier:
                        // Full fee for additional condition - no reduction
                        appliedModifiers.push('Physio 0009: Full fee for additional condition');
                        break;
                    
                    case '0010' as CoreModifier:
                        // 50% reduction for second condition
                        totalReduction *= 0.5;
                        appliedModifiers.push('Physio 0010: 50% reduction for second condition');
                        break;
                    
                    case '0013' as CoreModifier:
                        // Travelling costs for distances over 16km
                        appliedModifiers.push('Physio 0013: Travelling costs for distances over 16km');
                        break;
                    
                    case '0014' as CoreModifier:
                        // In-patient physiotherapy services
                        appliedModifiers.push('Physio 0014: In-patient physiotherapy services');
                        break;
                }
            });

            // Apply the total reduction to units and price
            if (totalReduction !== 1.0) {
                return {
                    ...line,
                    adjustedUnits: (line.initialUnits ?? line.units ?? 0) * totalReduction,
                    adjustedRand: (line.initialRand ?? 0) * totalReduction,
                    trace: ['init', ...appliedModifiers]
                };
            } else {
                return {
                    ...line,
                    adjustedUnits: line.initialUnits ?? line.units ?? 0,
                    adjustedRand: line.initialRand ?? 0,
                    trace: ['init', ...appliedModifiers]
                };
            }
        });
    }
} 